import React, { useContext } from "react"
import cn from "classnames"
import { connect } from "react-redux"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import ToolboxItem, { type ToolboxItemOwnProps, type ToolboxItemStateProps } from "@/signomatic/components/toolbox_item"
import ColorPicker from "@/signomatic/components/color_picker"
import ObjectBorderPicker from "@/signomatic/components/object_border_picker"
import type { State } from "@/signomatic/store/reducers"
import { SignContext, ActiveButtonContext } from "@/signomatic/contexts"
import type Sign from "@/signomatic/app/sign"
import { actions } from "@/signomatic/store/actions"
import { ResizeSensor } from "css-element-queries"
import type { TransitionProps } from "react-transition-group/Transition"
import tailwindTheme from "@/common/styles/theme.module.css"
import useMedia from "@/common/hooks/use_media"

const ToolboxTransition = (props: Partial<TransitionProps>) => (
  <CSSTransition timeout={{ enter: 300, exit: 300 }} {...(props as TransitionProps)} />
)

interface ToolboxOwnProps {
  children: React.ReactNode
  half?: boolean
  full?: boolean
  overflow?: boolean
  className?: string
  itemProps?: ToolboxItemOwnProps[]
}

interface ToolboxStateProps {
  subToolbox: string | null
  subToolboxStack: string[]
  subToolboxTarget: string | null
}

export interface ToolboxDispatchProps {
  setToolbox: typeof actions.setToolbox
  resetToolbox: typeof actions.resetToolbox
  setToolboxHeight: typeof actions.setToolboxHeight
}

type ToolboxProps = ToolboxOwnProps & ToolboxStateProps & ToolboxDispatchProps

interface ToolboxWrapperProps {
  desktop: boolean
  activeButton: React.RefObject<HTMLDivElement> | undefined
  sign: Sign
}

type ToolboxWrappedProps = ToolboxProps & ToolboxWrapperProps

interface ToolboxState {
  direction: string
  previousSubToolbox: string | null
  previousStackLength: number
  desktopTop: number | null
  arrowTop: number | null
}

class Toolbox extends React.PureComponent<ToolboxWrappedProps, ToolboxState> {
  static getDerivedStateFromProps(props: ToolboxProps, state: ToolboxState): ToolboxState {
    const length = props.subToolboxStack.length
    let direction = "left"

    if (props.subToolbox) {
      direction = length < state.previousStackLength ? "right" : "left"
    } else if (state.previousSubToolbox) {
      direction = "right"
    }
    return {
      direction,
      previousSubToolbox: props.subToolbox,
      previousStackLength: length,
      desktopTop: state.desktopTop,
      arrowTop: state.arrowTop,
    }
  }

  readonly state: ToolboxState = {
    direction: "left",
    previousSubToolbox: this.props.subToolbox,
    previousStackLength: this.props.subToolboxStack.length,
    desktopTop: null,
    arrowTop: null,
  }

  toolboxRef = React.createRef<HTMLDivElement>()
  sensor: ResizeSensor | null = null

  componentDidMount() {
    document.addEventListener("click", this.overlayClick, { capture: true })
    window.addEventListener("resize", this.updatePositions)
    this.updatePositions()

    if (this.toolboxRef.current) {
      this.sensor = new ResizeSensor(this.toolboxRef.current, this.updatePositions)
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.overlayClick, { capture: true })
    window.removeEventListener("resize", this.updatePositions)

    if (this.sensor) {
      this.sensor.detach(this.updatePositions)
    }
  }

  updatePositions = () => {
    const button = this.props.activeButton

    if (this.toolboxRef.current && button && button.current) {
      const menuHeight = 170

      const buttonCenter = button.current.getBoundingClientRect().top + button.current.clientHeight / 2

      const boxHeight = this.toolboxRef.current.clientHeight
      const height = boxHeight / 2
      const padding = 20

      const desktopTop = Math.max(
        menuHeight + padding + height,
        Math.min(buttonCenter, document.documentElement.clientHeight - padding * 2 - height)
      )

      const boxTop = desktopTop - 320 + this.toolboxRef.current.offsetTop
      const arrowTop = Math.min(Math.max(10, buttonCenter - boxTop - 30), boxHeight - 70)

      this.setState({ desktopTop, arrowTop })
    }
  }

  overlayClick = (e: MouseEvent) => {
    if (e.target instanceof Element) {
      if (
        !e.target.closest(".toolbox") &&
        !e.target.closest(".canvas") &&
        !e.target.closest(".toolbox-strip .menu_button.active") &&
        !e.target.closest(".signomatic-modal") &&
        !e.target.closest(".react-select") &&
        !e.target.closest(".signomatic-basket") &&
        !e.target.closest("#signomatic-basket") &&
        !e.target.closest("#video-modal") &&
        e.target !== document.body // for some reason react select clicks can have body as target
      ) {
        this.props.resetToolbox()
      }
    }
  }

  fullChildren(sign: Sign, desktop = false) {
    const toolboxes = this.props.subToolboxStack.concat(this.props.subToolbox || [])
    const target = this.props.subToolboxTarget
    const children = React.Children.toArray(this.props.children)

    if (toolboxes.includes("objectborder")) {
      children.push(<ObjectBorderPicker sign={sign} />)
    }

    if (toolboxes.includes("color") && (!desktop || !toolboxes.includes("customcolor")) && target !== "object") {
      children.push(<ColorPicker sign={sign} />)
    }

    if (toolboxes.includes("customcolor")) {
      children.push(<ColorPicker sign={sign} custom />)
    }

    return children
  }

  toolboxItemProps(index: number): ToolboxItemOwnProps {
    const { sign, full, half, overflow, className } = this.props
    const custom = this.props.itemProps ? this.props.itemProps[index] : {}
    return {
      full,
      half,
      overflow,
      className,
      sign,
      ...custom,
    }
  }

  toolboxItemStateProps(): ToolboxItemStateProps {
    const { setToolbox, setToolboxHeight } = this.props
    return { setToolbox, setToolboxHeight }
  }

  renderMobile(sign: Sign) {
    const children = this.fullChildren(sign)
    const childIndex = children.length - 1

    return (
      <TransitionGroup component={null}>
        <ToolboxTransition key={children.length} classNames="toolbox-animation">
          <ToolboxItem
            {...this.toolboxItemProps(childIndex)}
            {...this.toolboxItemStateProps()}
            index={childIndex}
            mobile
          >
            {children[childIndex]}
          </ToolboxItem>
        </ToolboxTransition>
      </TransitionGroup>
    )
  }

  renderDesktop(sign: Sign) {
    const children = this.fullChildren(sign, true)
    const inPlaceChildren = children.splice(2)

    return (
      <React.Fragment>
        {React.Children.map(children, (child, i) => (
          <ToolboxItem
            {...this.toolboxItemProps(i)}
            {...this.toolboxItemStateProps()}
            forwardedRef={i === 0 ? this.toolboxRef : null}
            index={i}
            arrowTop={i === 0 ? this.state.arrowTop : null}
          >
            {child}
          </ToolboxItem>
        ))}
        {inPlaceChildren.length > 0 && (
          <ToolboxItem
            half
            index={children.length + Math.max(0, inPlaceChildren.length - 1)}
            sign={sign}
            {...this.toolboxItemStateProps()}
          >
            {inPlaceChildren[inPlaceChildren.length - 1]}
          </ToolboxItem>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { desktop } = this.props
    const props = this.toolboxItemProps(0)

    return (
      <div
        className={cn("toolbox-container", this.state.direction, {
          full: props.full,
          half: props.half,
        })}
        style={{ top: desktop && this.state.desktopTop ? this.state.desktopTop : undefined }}
      >
        {desktop ? this.renderDesktop(this.props.sign) : this.renderMobile(this.props.sign)}
      </div>
    )
  }
}

const ToolboxWrapper: React.FC<ToolboxProps> = props => {
  const sign = useContext(SignContext)
  const activeButton = useContext(ActiveButtonContext)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensLg})`)

  if (!sign) {
    throw new Error("Sign instance undefined")
  }

  return <Toolbox desktop={desktop} activeButton={activeButton} sign={sign} {...props} />
}

export default connect<ToolboxStateProps, ToolboxDispatchProps, ToolboxOwnProps>(
  ({ subToolbox, subToolboxStack, subToolboxTarget }: State) => ({
    subToolbox,
    subToolboxStack,
    subToolboxTarget,
  }),
  {
    setToolbox: actions.setToolbox,
    resetToolbox: actions.resetToolbox,
    setToolboxHeight: actions.setToolboxHeight,
  }
)(ToolboxWrapper)
